export function canStartPointer(event, { inputMode = 'all', activePointerType = null, ignoreTouchUntil = 0, now = 0 } = {}) {
  const pointerType = event?.pointerType || 'mouse';
  if (!['pen', 'touch', 'mouse'].includes(pointerType)) return false;
  if (inputMode === 'pencil' && pointerType !== 'pen') return false;
  if (pointerType === 'mouse' && Number(event?.button) > 0 && Number(event?.button) !== 5) return false;
  if (pointerType === 'touch' && event?.isPrimary === false) return false;
  if (pointerType === 'touch' && now < ignoreTouchUntil) return false;
  if (!activePointerType) return true;
  return pointerType === 'pen' && activePointerType === 'touch';
}

// A new pen contact is authoritative: Pointer Events guarantees that a
// pointerdown starts a new active-button sequence. If WebKit failed to deliver
// the previous pointerup/cancel, the old stroke must be stale. Apple Pencil may
// reuse the same pointerId across contacts, so comparing ids is not enough.
export function shouldRecoverActivePointer(event, { activePointerId = null, activePointerType = null } = {}) {
  if (activePointerId === null || event?.pointerType !== 'pen') return false;
  return ['pen', 'touch', 'mouse'].includes(activePointerType);
}

export function selectIOSTouch(touchList, { inputMode = 'all' } = {}) {
  const touches = Array.from(touchList || []);
  const stylus = touches.find((touch) => touch?.touchType === 'stylus');
  if (stylus) return stylus;
  if (inputMode === 'pencil') return null;
  return touches.find((touch) => touch?.touchType !== 'stylus') || null;
}

export function findTouchByIdentifier(touchList, identifier) {
  if (identifier === null || identifier === undefined) return null;
  return Array.from(touchList || []).find((touch) => touch?.identifier === identifier) || null;
}

export function collectPointerSamples(event, { useCoalesced = true } = {}) {
  let coalesced = [];
  try {
    coalesced = useCoalesced && typeof event?.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [];
  } catch (_) {
    coalesced = [];
  }

  const candidates = Array.isArray(coalesced) && coalesced.length ? [...coalesced] : [event];
  const seen = new Set();
  return candidates.filter((sample) => {
    if (!Number.isFinite(sample?.clientX) || !Number.isFinite(sample?.clientY)) return false;
    const key = `${sample.clientX}:${sample.clientY}:${Number(sample.timeStamp) || 0}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export function resolveStrokeWidth(event, { pointerType = 'mouse' } = {}) {
  const defaultPressure = 0.5;

  // PointerEvent.pressure is normalized, but WebKit Touch.force only promises
  // a positive linear scale. Finger touches therefore use a stable width;
  // stylus values keep pressure variation after being clamped to [0, 1].
  if (pointerType === 'mouse' || pointerType === 'touch') {
    return 1.25 + defaultPressure * 3.4;
  }

  const reportedPressure = Number.isFinite(event?.pressure)
    ? event.pressure
    : Number.isFinite(event?.force) ? event.force : 0;
  const pressure = reportedPressure > 0
    ? Math.max(0, Math.min(1, reportedPressure))
    : defaultPressure;
  return 1.25 + pressure * 3.4;
}
